import clsx from "clsx";
import {
	Bar,
	Brush,
	Cell,
	LabelList,
	BarChart as RechartsBarChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { ChartWrapper, type ChartWrapperProps } from "../chart-wrapper";
import { CartesianGrid } from "../components/cartesian-grid";
import { TooltipContent } from "../components/tooltip-content";
import { CHART_RADIUS } from "../constants";
import { findMinMaxValues } from "../handlers";
import type { ChartData, ChartDataProps } from "../types";

/** Props Interface */
interface BarChartProps<T extends ChartData>
	extends Omit<ChartWrapperProps, "children" | "isLoading">,
		ChartDataProps<T> {
	withBrush?: boolean;
	colors?: string[];
	onClick?: (data: T) => void;
	formatter?: (value: number) => string;
}

export function BarChart<T extends { name: string }>({
	withBrush,
	title,
	description,
	data,
	dataKeys,
	colors,
	formatter,
	onClick,
}: BarChartProps<T>) {
	/** Hooks */
	const { min, max } = findMinMaxValues(data ?? []);

	/** Render */
	return (
		<ChartWrapper
			title={title}
			description={description}
			isLoading={!data}
		>
			<ResponsiveContainer
				width="100%"
				height="100%"
				className="relative"
			>
				<RechartsBarChart
					data={data}
					margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
				>
					<CartesianGrid />
					<YAxis
						domain={[min, max]}
						allowDataOverflow={false}
						tickLine={false}
						axisLine={false}
						className="text-xs"
						width={120}
						hide
						tickFormatter={formatter}
					/>
					<Tooltip
						cursor={{
							className: "fill-primaryGray-500/10",
						}}
						content={(props) => (
							<TooltipContent
								{...props}
								formatter={formatter}
							/>
						)}
						animationDuration={100}
						animationEasing="ease-out"
					/>
					<XAxis
						dataKey="name"
						className="text-xs"
						tickLine={false}
						axisLine={false}
					/>
					{dataKeys?.map((key, index) => (
						<Bar
							key={key as string}
							dataKey={key as string}
							radius={CHART_RADIUS}
							animationBegin={200 * index}
							onClick={onClick}
							cursor={onClick ? "pointer" : "default"}
						>
							<LabelList
								dataKey={key as string}
								position="middle"
								angle={-90}
								fill="var(--color-white) !important"
								fontSize={10}
								formatter={formatter}
							/>
							{data?.map((entry, index) => {
								/** Render */
								return (
									<Cell
										key={entry.name}
										className={clsx("hover:opacity-90 cursor-pointer")}
										fill={colors?.[index]}
										radius={CHART_RADIUS}
									/>
								);
							})}
						</Bar>
					))}
					{withBrush && (
						<Brush
							dataKey="name"
							height={10}
							stroke="var(--color-primary-500)"
							fill="rgba(0,0,0,0)"
							className="text-xs"
						/>
					)}
				</RechartsBarChart>
			</ResponsiveContainer>
		</ChartWrapper>
	);
}
